import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { OpenMarketSearchLandingButton } from '../../../../components/common/buttons/OpenMarketSearchLandingButton';
import { EmptyState } from '../../../../components/common/empty/EmptyState';
import { BrandFilterToggleSection } from '../../../../components/common/filter/BrandFilterToggleSection';
import { FilterSection } from '../../../../components/common/filter/FilterSection/FilterSection';
import { FilterSlideModal } from '../../../../components/common/filter/FilterSlideModal/FilterSlideModal';
import { MainHead } from '../../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../../components/common/loading/AnimateLoading';
import { IDirectDealFilter } from '../../../../constants/filter/directDealFilter.constant';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useGetNewOpenMarketProductQuery } from '../../../../hooks/api/product/queries/useGetNewOpenMarketProduct.query';
import { useInfiniteOpenMarketProducts } from '../../../../hooks/api/product/queries/useInfiniteOpenMarketProducts';
import { useAddFilterOptions } from '../../../../hooks/filter/useAddFilterOptions';
import { useCheckSearchParamUpdate } from '../../../../hooks/filter/useCheckSearchParamUpdate';
import { useUpdateDirectDealFilterStates } from '../../../../hooks/filter/useUpdateDirectDealFilterStates';
import { useUpdateDirectDealListPageSearchParam } from '../../../../hooks/filter/useUpdateDirectDealListPageSearchParam';
import { useGetDownloadAppModalHeight } from '../../../../hooks/modal/useGetDownloadAppModalHeight';
import { useMarkNotificationAsRead } from '../../../../hooks/page/useMarkNotificationAsRead';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useInfiniteScrollLoadingTarget } from '../../../../hooks/scroll/useInfiniteScrollLoadingTarget';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { filterSlice } from '../../../../store/filterSlice';
import { createDirectDealFilterSearchParams } from '../../../../utils/filter/createDirectDealFilterSearchParams.util';
import { isFilterSelectedAsAll } from '../../../../utils/filter/isFilterSelectedAsAll.util';
import {
  IOpenMarketProductForDetailCard,
  OpenMarketProductDetailCard,
} from '../../../../views/Product/listView/OpenMarketProductDetailCard/OpenMarketProductDetailCard';
import { OpenMarketProductListDetailCardGroup } from '../../../../views/Product/listView/OpenMarketProductDetailCard/OpenMarketProductListDetailCardGroup';
import { OpenMarketCarouselBanners } from '../../components/OpenMarketCarouselBanners';
import { OpenMarketListWrapper } from '../../components/OpenMarketListWrapper';
import { SellTractorButton } from '../../components/SellTractorButton';
import { DirectDealCurationSection } from './components/DirectDealCurationSection';
import { DirectDealListCountSortSection } from './components/DirectDealListCountSortSection';
import { DirectDealRecentProductSection } from './components/DirectDealRecentProductSection';
import { DirectDealSelectedFilterChipSection } from './components/DirectDealSelectedFilterChipSection';

const FIXED_HEADER_HEIGHT = 48;

export const DirectDealListPage = () => {
  const dispatch = useAppDispatch();
  const filterActions = filterSlice.actions;
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const { isGetBrandsQueryLoading } = useAddFilterOptions({
    filterType: 'directDealFilter',
    data: 'brands',
    applicableMachineType: 'ALL',
  });
  useCheckSearchParamUpdate({ filterType: 'directDealFilter' });
  useUpdateDirectDealFilterStates();
  useUpdateDirectDealListPageSearchParam();

  const directDealFilterStates = useAppSelector((state) => state.filter.directDealFilter);

  const { data: newOpenMarketProduct } = useGetNewOpenMarketProductQuery();
  useMarkNotificationAsRead(newOpenMarketProduct, 'newOpenMarketProductInfo');

  const filterSearchParams = createDirectDealFilterSearchParams({
    filters: directDealFilterStates.globalFilters as IDirectDealFilter[],
  });

  const {
    data: products,
    isLoading: isGetProductsQueryLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteOpenMarketProducts({
    searchParam: {
      ...filterSearchParams,
      count: 4,
    },
  });

  const { loadingTargetRef, isLoadingTargetRendered } = useInfiniteScrollLoadingTarget({
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    triggeredFnWhenInView: fetchNextPage,
  });

  const downloadAppModalHeight = useGetDownloadAppModalHeight();
  const stickyTop = FIXED_HEADER_HEIGHT + downloadAppModalHeight;

  // 조건·브랜드·정렬 중 하나라도 기본값과 다르면 필터가 적용된 것으로 본다.
  // 하나도 고르지 않았거나 모두 고른 항목은 '전체'로 보아 칩이 없으므로, 적용된 필터로 치지 않는다.
  const hasActiveFilter = useMemo<boolean>(() => {
    return directDealFilterStates.globalFilters.some((filter) => {
      if (filter.optionType === 'slider') {
        return filter.options.some((option) => option.value !== option.defaultValue);
      }

      if (isFilterSelectedAsAll(filter)) {
        return false;
      }

      return filter.options.some((option) => option.isSelected !== Boolean(option.isDefault));
    });
  }, [directDealFilterStates.globalFilters]);

  // 필터를 적용하면 결과에 집중할 수 있도록 검색창·큐레이션·최근 7일 영역을 접는다.
  // 필터가 걸린 채 다시 들어오면 처음부터 접힌 상태로 그려, 진입하자마자 접히는 애니메이션이 보이지 않게 한다.
  const [isTopAreaCollapsed, setIsTopAreaCollapsed] = useState(hasActiveFilter);
  // 접히는 영역이 이미 스크롤로 가려져 있었다면, 최상단으로 이동하며 잠깐 보였다가 접히는 모습이 어색해 애니메이션 없이 접는다.
  const [isTopAreaCollapseAnimated, setIsTopAreaCollapseAnimated] = useState(true);
  const [isSellTractorButtonVisible, setIsSellTractorButtonVisible] = useState(false);
  const [brandSectionHeight, setBrandSectionHeight] = useState(0);
  const [filterSectionHeight, setFilterSectionHeight] = useState(0);
  // 접을 때 화면 위로 가려져 있던 높이. 이만큼 미리 잘라 내 보이던 부분만 제자리에서 접히게 한다.
  const [topAreaHiddenHeight, setTopAreaHiddenHeight] = useState(0);

  const brandSectionRef = useRef<HTMLDivElement | null>(null);
  const filterSectionRef = useRef<HTMLDivElement | null>(null);
  const productListContainerRef = useRef<HTMLDivElement | null>(null);
  const collapsibleContentRef = useRef<HTMLDivElement | null>(null);
  // 필터 적용 시 최상단으로 먼저 스크롤되기도 하므로, 스크롤할 때마다 접히는 영역이 얼마나 가려져 있는지 미리 기억해 둔다.
  const topAreaScrollStateRef = useRef({ scrollY: 0, hiddenHeight: 0, isVisible: true });
  const scrollYAfterCollapseRef = useRef<number | null>(null);
  const isFirstTopAreaEffectRef = useRef(true);

  const expandTopArea = () => {
    setTopAreaHiddenHeight(0);
    setIsTopAreaCollapseAnimated(true);
    setIsTopAreaCollapsed(false);
  };

  // 필터를 바꾸며 최상단으로 이동한 화면이 그려지기 전에 접기·스크롤 보정을 끝내야 상단이 잠깐 보이지 않는다.
  useLayoutEffect(() => {
    const isFirstRun = isFirstTopAreaEffectRef.current;
    isFirstTopAreaEffectRef.current = false;

    if (hasActiveFilter) {
      // 진입할 때부터 접힌 상태로 시작했으므로 따로 접거나 스크롤하지 않는다.
      if (isFirstRun) {
        return;
      }

      const { scrollY, hiddenHeight, isVisible } = topAreaScrollStateRef.current;

      // 이미 다 가려져 있었다면 최상단으로 이동하며 잠깐 보였다가 접히는 모습이 어색해 애니메이션 없이 접는다.
      if (!isVisible) {
        setIsTopAreaCollapseAnimated(false);
        setIsTopAreaCollapsed(true);
        window.scrollTo(0, 0);
        return;
      }

      // 가려져 있던 윗부분은 미리 잘라 내고 그만큼 스크롤을 올려, 화면은 그대로 둔 채 보이던 부분만 접는다.
      setTopAreaHiddenHeight(hiddenHeight);
      scrollYAfterCollapseRef.current = scrollY - hiddenHeight;
      setIsTopAreaCollapseAnimated(true);
      setIsTopAreaCollapsed(true);
      return;
    }

    // 필터를 모두 해제한 뒤 최상단에 있으면 접었던 영역을 다시 보여 준다.
    if (window.scrollY <= 0) {
      expandTopArea();
    }
  }, [hasActiveFilter]);

  // 잘라 낸 높이가 반영된 직후, 그려지기 전에 스크롤을 맞춰야 화면이 튀지 않는다.
  useLayoutEffect(() => {
    if (scrollYAfterCollapseRef.current === null) {
      return;
    }

    window.scrollTo(0, scrollYAfterCollapseRef.current);
    scrollYAfterCollapseRef.current = null;
  }, [topAreaHiddenHeight]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasActiveFilter && window.scrollY <= 0) {
        expandTopArea();
      }

      // 접히는 영역은 헤더 밑으로 들어간 만큼 가려진 것으로 본다.
      const collapsibleContentRect = collapsibleContentRef.current?.getBoundingClientRect();

      if (collapsibleContentRect) {
        topAreaScrollStateRef.current = {
          scrollY: window.scrollY,
          hiddenHeight: Math.min(Math.max(stickyTop - collapsibleContentRect.top, 0), collapsibleContentRect.height),
          isVisible: collapsibleContentRect.bottom > stickyTop,
        };
      }

      // 매물 목록을 보기 시작한 뒤에 판매 버튼을 노출한다.
      // (매물 카운트를 포함한 필터 영역이 브랜드 영역 바로 아래에 고정된 시점)
      const filterSectionTop = filterSectionRef.current?.getBoundingClientRect().top;

      if (filterSectionTop !== undefined) {
        setIsSellTractorButtonVisible(filterSectionTop <= stickyTop + brandSectionHeight);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    // 큐레이션·새로 올라온 매물 영역이 뒤늦게 그려지거나 접히면 스크롤 없이도 위치가 바뀌므로 다시 계산한다.
    const resizeObserver = new ResizeObserver(handleScroll);
    if (collapsibleContentRef.current) {
      resizeObserver.observe(collapsibleContentRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [hasActiveFilter, isTopAreaCollapsed, isGetBrandsQueryLoading, stickyTop, brandSectionHeight]);

  // 알약·칩·카운트 묶음을 브랜드 영역 바로 아래에 고정하려면 브랜드 영역의 높이가 필요하다.
  useEffect(() => {
    const brandSectionElement = brandSectionRef.current;

    if (!brandSectionElement) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      setBrandSectionHeight(brandSectionElement.offsetHeight);
    });
    resizeObserver.observe(brandSectionElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isGetBrandsQueryLoading]);

  useEffect(() => {
    const filterSectionElement = filterSectionRef.current;

    if (!filterSectionElement) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      setFilterSectionHeight(filterSectionElement.offsetHeight);
    });
    resizeObserver.observe(filterSectionElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isGetBrandsQueryLoading]);

  // 목록 아래쪽까지 스크롤한 채 필터를 바꾸면 스크롤 위치가 유지돼 새 목록의 첫 매물이 고정 영역 뒤에 가려진다.
  // 필터가 바뀌면 첫 매물이 필터 영역 바로 아래에 오도록 스크롤을 올린다.
  const filterSearchParamsKey = JSON.stringify(filterSearchParams);
  const previousFilterSearchParamsKeyRef = useRef(filterSearchParamsKey);
  const shouldAlignProductListRef = useRef(false);

  const alignProductListBelowFilterSection = () => {
    const productListTop = productListContainerRef.current?.getBoundingClientRect().top;
    const filterSectionBottom = filterSectionRef.current?.getBoundingClientRect().bottom;

    if (productListTop === undefined || filterSectionBottom === undefined || productListTop >= filterSectionBottom) {
      return;
    }

    window.scrollTo(0, window.scrollY + productListTop - filterSectionBottom);
  };

  useEffect(() => {
    if (previousFilterSearchParamsKeyRef.current === filterSearchParamsKey) {
      return;
    }

    previousFilterSearchParamsKeyRef.current = filterSearchParamsKey;
    shouldAlignProductListRef.current = true;
    alignProductListBelowFilterSection();
  }, [filterSearchParamsKey]);

  // 새 목록이 그려지면 높이가 바뀌고, iOS는 관성 스크롤 중의 스크롤 이동을 무시하기도 하므로 한 번 더 맞춘다.
  useEffect(() => {
    if (!shouldAlignProductListRef.current || isGetProductsQueryLoading) {
      return;
    }

    shouldAlignProductListRef.current = false;
    alignProductListBelowFilterSection();
  }, [products, isGetProductsQueryLoading]);

  const handleFilterButtonClick = () => {
    setIsFilterModalOpen(true);
    window.scrollTo(0, 0);
  };

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} headerTitle={'회원 직거래'} hasMyInfoButton />,
    fixedHeaderHeight: FIXED_HEADER_HEIGHT,
    // 필터를 적용하면 목록을 보고 있는 것이므로 스크롤 위치와 관계없이 판매 버튼을 노출한다.
    fixedFooter: hasActiveFilter || isSellTractorButtonVisible ? <SellTractorButton pageType={'olp'} /> : undefined,
    fixedFooterHeight: 0,
    style: { background: '#fff' },
  });

  if (isGetBrandsQueryLoading) {
    return <AnimateLoading />;
  }

  return (
    <>
      <Helmet>
        <title>{pageInfo.openMarketListPage.title}</title>
      </Helmet>

      <OpenMarketCarouselBanners category={'CAROUSEL_MAIN'} outputLocation={'open-market'} />

      <CollapsibleArea isCollapsed={isTopAreaCollapsed} isAnimated={isTopAreaCollapseAnimated}>
        <CollapsibleAreaInner ref={collapsibleContentRef} style={{ marginTop: -topAreaHiddenHeight }}>
          <SearchButtonSection>
            <OpenMarketSearchLandingButton pageType="OLP" />
          </SearchButtonSection>

          <DirectDealCurationSection />

          <DirectDealRecentProductSection />
        </CollapsibleAreaInner>
      </CollapsibleArea>

      <StickySection ref={brandSectionRef} style={{ top: `${stickyTop}px`, zIndex: 56 }}>
        <BrandFilterToggleSection filterType={'directDealFilter'} />
      </StickySection>

      <StickySection ref={filterSectionRef} style={{ top: `${stickyTop + brandSectionHeight}px`, zIndex: 55 }}>
        <FilterSection filterType={'directDealFilter'} />

        <DirectDealSelectedFilterChipSection />

        <DirectDealListCountSortSection />
      </StickySection>

      <OpenMarketListWrapper>
        <ProductListContainer
          ref={productListContainerRef}
          fixedAreaBottom={stickyTop + brandSectionHeight + filterSectionHeight}
        >
          <OpenMarketProductListDetailCardGroup>
            {products &&
              products.pages.map((page, pageIndex) => {
                return page.map((product) => {
                  return (
                    <OpenMarketProductDetailCard
                      key={product.id}
                      product={product as IOpenMarketProductForDetailCard}
                      isNewBadgeVisible
                      indexInList={pageIndex * 4 + page.indexOf(product)}
                    />
                  );
                });
              })}
            {!products?.pages?.[0]?.length && (
              <EmptyState
                title={'찾으시는 결과가 없네요.'}
                buttonText={'다시 시도하기'}
                onClick={handleFilterButtonClick}
              />
            )}
            {isLoadingTargetRendered && <LoadingTarget ref={loadingTargetRef} />}
          </OpenMarketProductListDetailCardGroup>
        </ProductListContainer>
      </OpenMarketListWrapper>

      {isFilterModalOpen && (
        <FilterSlideModal
          isShow={isFilterModalOpen}
          filterType={'directDealFilter'}
          closeModal={() => setIsFilterModalOpen(false)}
        />
      )}
    </>
  );
};

const SearchButtonSection = styled.div`
  display: flex;
  padding: 0px 16px 16px 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

// 높이를 알 수 없는 영역을 부드럽게 접기 위해 grid 행 높이를 전환한다.
const CollapsibleArea = styled.div<{ isCollapsed: boolean; isAnimated: boolean }>`
  width: 100%;

  display: grid;
  grid-template-rows: ${(props) => (props.isCollapsed ? '0fr' : '1fr')};

  /* 잘라 낸 윗부분(음수 여백)이 위쪽 영역 위에 그려지지 않도록 가린다. */
  overflow: hidden;

  transition: ${(props) => (props.isAnimated ? 'grid-template-rows 0.3s ease-in-out' : 'none')};
`;

const CollapsibleAreaInner = styled.div`
  min-height: 0;

  overflow: hidden;
`;

const StickySection = styled.div`
  width: 100%;

  position: sticky;

  background-color: white;
`;

// 매물이 적어도 목록이 고정 영역 아래 화면을 채우도록 한다.
// 짧으면 끝까지 스크롤할 때 고정 영역이 놓일 자리가 모자라 필터 영역이 브랜드 영역 밑으로 밀려 들어가며 매물을 가린다.
const ProductListContainer = styled.div<{ fixedAreaBottom: number }>`
  min-height: ${(props) => `calc(100vh - ${props.fixedAreaBottom}px)`};

  display: flex;
  padding: 16px 16px 32px 16px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const LoadingTarget = styled.div``;
